import { useAtom } from 'jotai';
import { selectedFindingIdAtom } from '@/hooks/atoms';
import { SEVERITY_COLORS, type Finding } from '@/types/forensics';

interface BoundingBoxLayerProps {
  findings: Finding[];
  containerWidth: number;
  containerHeight: number;
}

export default function BoundingBoxLayer({ findings, containerWidth, containerHeight }: BoundingBoxLayerProps) {
  const [selectedId, setSelectedId] = useAtom(selectedFindingIdAtom);

  const regionFindings = findings.filter((f) => f.region);

  if (containerWidth === 0 || containerHeight === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={containerWidth}
      height={containerHeight}
      viewBox={`0 0 ${containerWidth} ${containerHeight}`}
    >
      <defs>
        {/* Glow filter for selected box */}
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {regionFindings.map((finding) => {
        const r = finding.region!;
        const x = r.x * containerWidth;
        const y = r.y * containerHeight;
        const w = r.w * containerWidth;
        const h = r.h * containerHeight;
        const color = SEVERITY_COLORS[finding.severity];
        const isSelected = selectedId === finding.id;
        const pct = Math.round(finding.confidence * 100);

        // Truncate label to fit
        const maxLabelChars = Math.max(10, Math.floor(w / 7));
        const labelText = finding.title.length > maxLabelChars ? finding.title.slice(0, maxLabelChars) + '...' : finding.title;
        const fullLabel = `${labelText} | ${pct}%`;
        const labelWidth = Math.min(fullLabel.length * 6.5 + 8, w + 40);

        return (
          <g
            key={finding.id}
            className="pointer-events-auto cursor-pointer"
            onClick={() => setSelectedId(finding.id)}
            filter={isSelected ? 'url(#glow)' : undefined}
          >
            {/* Fill — semi-transparent region highlight */}
            <rect x={x} y={y} width={w} height={h} fill={color} fillOpacity={isSelected ? 0.2 : 0.1} rx={3} />
            {/* Border — rounded, dashed when not selected */}
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              fill="none"
              stroke={color}
              strokeWidth={isSelected ? 3 : 2}
              strokeDasharray={isSelected ? undefined : '6 3'}
              rx={3}
            />
            {/* Corner markers for better visual anchoring */}
            {[
              [x, y],
              [x + w, y],
              [x, y + h],
              [x + w, y + h],
            ].map(([cx, cy], i) => (
              <circle key={i} cx={cx} cy={cy} r={isSelected ? 4 : 3} fill={color} fillOpacity={0.8} />
            ))}
            {/* Label background — positioned above or inside the box */}
            <rect x={x} y={y > 22 ? y - 22 : y + h + 2} width={labelWidth} height={20} fill={color} fillOpacity={0.85} rx={3} />
            {/* Label text */}
            <text
              x={x + 5}
              y={y > 22 ? y - 7 : y + h + 16}
              fill="white"
              fontSize={11}
              fontWeight={600}
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {fullLabel}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
